/**
 * יומן שליחות — message_log ∪ un-routed whatsapp_messages. Filters by
 * channel / status / type / clinic / range / fallback-only; failures first;
 * "שלח שוב" re-runs the router for a stored row.
 * Error codes: CK-10504 (resend).
 */
import { useMemo, useState } from "react";
import { ArrowCounterClockwise, PaperPlaneTilt, CaretDown, Warning } from "@phosphor-icons/react";
import ContentLoader from "../../../../components/shared/ContentLoader";
import LoaderLoop from "../../../../components/shared/loader/LoaderLoop";
import MessagingStatusBadge from "../../../../components/ui/MessagingStatusBadge";
import { useToast } from "../../../../contexts/ToastContext";
import { useGetMessagingOutboxQuery, useResendMessageMutation } from "../../../../store/services/nivApi";
import { MESSAGE_TYPES, MESSAGE_TYPE_IDS, CHANNEL_LABELS, OUTCOME_LABELS, describeReason, formatWhen, maskPhone, stampFromRow } from "./messagesShared";

const DAYS = [
  [1, "24 שעות"],
  [7, "7 ימים"],
  [30, "30 יום"],
];

export default function OutboxPanel({ isAuthorized }) {
  const { showSuccess, showError } = useToast();
  const [channel, setChannel] = useState("");
  const [status, setStatus] = useState("");
  const [type, setType] = useState("");
  const [clinicId, setClinicId] = useState("");
  const [days, setDays] = useState(7);
  const [fallbackOnly, setFallbackOnly] = useState(false);
  const [open, setOpen] = useState(() => new Set());

  const args = useMemo(
    () => ({ channel: channel || undefined, status: status || undefined, type: type || undefined, clinicId: clinicId.trim() || undefined, days, fallbackOnly, limit: 200 }),
    [channel, status, type, clinicId, days, fallbackOnly]
  );
  const { data: rows = [], isLoading, isFetching, refetch } = useGetMessagingOutboxQuery(args, { skip: !isAuthorized });
  const [resend, { isLoading: resending }] = useResendMessageMutation();

  const sorted = useMemo(() => [...rows].sort((a, b) => (a.status === "sent" ? 1 : 0) - (b.status === "sent" ? 1 : 0) || (b.createdAt || "").localeCompare(a.createdAt || "")), [rows]);
  const failedCount = rows.filter((r) => r.status !== "sent").length;
  const fallbackCount = rows.filter((r) => r.fallbackUsed).length;

  const toggle = (id) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const handleResend = async (row) => {
    try {
      const res = await resend({ logId: row.id }).unwrap();
      const out = res?.result?.outcome;
      if (res?.result?.ok) showSuccess(`נשלח שוב (${OUTCOME_LABELS[out] || out})`);
      else showError(`השליחה החוזרת נכשלה (${OUTCOME_LABELS[out] || out || "—"})`);
    } catch (error) {
      console.error("[CK-10504] Outbox resend failed:", {
        errorCode: "CK-10504",
        errorMessage: error?.message,
        logId: row.id,
        timestamp: new Date().toISOString(),
      });
      showError(error?.message || "השליחה החוזרת נכשלה");
    }
  };

  return (
    <div className="nmsg-panel">
      <div className="nmsg-toolbar nmsg-toolbar-wrap">
        <select className="nmsg-select" value={channel} onChange={(e) => setChannel(e.target.value)} aria-label="ערוץ">
          <option value="">כל הערוצים</option>
          <option value="whatsapp">{CHANNEL_LABELS.whatsapp}</option>
          <option value="sms">{CHANNEL_LABELS.sms}</option>
        </select>
        <select className="nmsg-select" value={status} onChange={(e) => setStatus(e.target.value)} aria-label="סטטוס">
          <option value="">כל הסטטוסים</option>
          <option value="sent">נשלח</option>
          <option value="failed">נכשל</option>
          <option value="failed_both">נכשל בשני הערוצים</option>
        </select>
        <select className="nmsg-select" value={type} onChange={(e) => setType(e.target.value)} aria-label="סוג הודעה">
          <option value="">כל הסוגים</option>
          {MESSAGE_TYPE_IDS.map((id) => (
            <option key={id} value={id}>
              {MESSAGE_TYPES[id].label}
            </option>
          ))}
        </select>
        <input className="nmsg-input" dir="ltr" placeholder="clinicId" value={clinicId} onChange={(e) => setClinicId(e.target.value)} aria-label="מזהה קליניקה" />
        <div className="nmsg-filters" role="group" aria-label="טווח">
          {DAYS.map(([d, l]) => (
            <button key={d} type="button" className={`nmsg-filter ${days === d ? "active" : ""}`} onClick={() => setDays(d)}>
              {l}
            </button>
          ))}
          <button type="button" className={`nmsg-filter tone-fallback ${fallbackOnly ? "active" : ""}`} onClick={() => setFallbackOnly((v) => !v)} aria-pressed={fallbackOnly}>
            גיבוי / כשלים בלבד
          </button>
        </div>
        <button type="button" className="niv-btn niv-btn-secondary niv-btn-sm" onClick={() => refetch()} disabled={isFetching}>
          <ArrowCounterClockwise size={14} className={isFetching ? "spinning" : ""} /> רענון
        </button>
      </div>

      <div className="nmsg-summary">
        <span className="nmsg-chip nmsg-chip-muted">{rows.length} שליחות</span>
        <span className="nmsg-chip nmsg-chip-fallback">{fallbackCount} גיבוי SMS</span>
        <span className="nmsg-chip nmsg-chip-bad">{failedCount} כשלים</span>
      </div>

      {isLoading ? (
        <ContentLoader size="sm" compact message="טוען…" />
      ) : sorted.length === 0 ? (
        <div className="nmsg-empty">
          <PaperPlaneTilt size={36} weight="light" />
          <p>אין שליחות בטווח ובסינון שנבחרו.</p>
        </div>
      ) : (
        <div className="nmsg-rows">
          {sorted.map((row) => {
            const isOpen = open.has(row.id);
            const tone = row.status !== "sent" ? "failed" : row.fallbackUsed ? "fallback" : row.channel === "sms" ? "sms" : "whatsapp";
            const t = MESSAGE_TYPES[row.messageType];
            return (
              <div key={`${row.source}-${row.id}`} className={`nmsg-row nmsg-row-${tone}`}>
                <span className={`nmsg-dot nmsg-dot-${tone}`} aria-hidden="true" />
                <div className="nmsg-row-main">
                  <div className="nmsg-row-top">
                    <strong className="nmsg-row-name">{t?.label || row.messageType || row.templateId || row.contextType || "—"}</strong>
                    <MessagingStatusBadge messaging={stampFromRow(row)} />
                    {row.source === "whatsapp" && <span className="nmsg-chip nmsg-chip-muted">ישיר (ללא ראוטר)</span>}
                    {row.resendOf && <span className="nmsg-chip nmsg-chip-muted">שליחה חוזרת</span>}
                    <span className="nmsg-row-time">{formatWhen(row.createdAt)}</span>
                  </div>
                  <div className="nmsg-row-meta">
                    <span dir="ltr">{maskPhone(row.to)}</span>
                    {row.clinicName || row.clinicId ? <span>{row.clinicName || row.clinicId}</span> : null}
                    {row.fallbackReason && <span className="nmsg-reason">{describeReason(row.fallbackReason)}</span>}
                    {row.metaErrorCode != null && <span dir="ltr">Meta {row.metaErrorCode}</span>}
                    {row.error && row.status !== "sent" && (
                      <span className="nmsg-bad">
                        <Warning size={11} weight="fill" /> {row.error}
                      </span>
                    )}
                  </div>
                  {isOpen && (
                    <div className="nmsg-row-detail">
                      {row.channelsTried?.length > 0 && (
                        <div className="nmsg-tried">
                          {row.channelsTried.map((c, i) => (
                            <span key={i} className={`nmsg-chip nmsg-chip-${c.result === "sent" ? c.channel : c.result === "failed" ? "bad" : "muted"}`}>
                              {CHANNEL_LABELS[c.channel]} · {c.result === "sent" ? "נשלח" : c.result === "failed" ? "נכשל" : "לא זמין"}
                              {c.reason ? ` (${describeReason(c.reason)})` : ""}
                              {c.metaCode != null ? ` (${c.metaCode})` : ""}
                            </span>
                          ))}
                        </div>
                      )}
                      <div className="nmsg-row-meta">
                        <span dir="ltr">id {row.id}</span>
                        {row.refId && <span dir="ltr">ref {row.refId}</span>}
                        {row.nivErrorLogId && <span dir="ltr">error-log {row.nivErrorLogId}</span>}
                        {row.whatsappDocId && <span dir="ltr">wa {row.whatsappDocId}</span>}
                      </div>
                    </div>
                  )}
                </div>
                <div className="nmsg-row-actions">
                  {row.source === "router" && row.status !== "sent" && (
                    <button type="button" className="niv-btn niv-btn-primary niv-btn-sm" onClick={() => handleResend(row)} disabled={resending}>
                      {resending ? <LoaderLoop size="xs" /> : <PaperPlaneTilt size={14} />} שלח שוב
                    </button>
                  )}
                  <button type="button" className="nmsg-expand" onClick={() => toggle(row.id)} aria-expanded={isOpen} aria-label="פרטים">
                    <CaretDown size={16} weight="bold" className={isOpen ? "is-open" : ""} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
